import React, { useContext, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Grid, Checkbox, FormControlLabel, FormControl, MenuItem } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import OrderListItem from './OrderListItem';
import { PlanningDataContext, PlanningFilterContext, PlanningSelectionContext, PlanningUiContext } from '../context';
import { MODALS, ROUTE_COLORS } from '../constants';
import { css } from '@emotion/react';
import { Body1, Body2, GridItemRow, PlanningListItem, PrimaryButtonFullW, SecondaryButtonFullW } from '../blocks';
import Select from '@material-ui/core/Select';
import SearchInput from '@/components/misc/SearchInput';
import { useClientUser } from '@/hooks';
import { PLAN_PAGE_PAST_SCHEDULED, PLAN_PAGE_UNSCHEDULED } from '@/constants/featureFlags';
import { ROUTE_FILTER_ALL } from '../util/routeFilter';

const CATEGORY_LABELS = {
    ORDER_TYPE: 'Order Type',
    WAREHOUSING_TYPE: 'Warehousing',
    SCHEDULING_TYPE: 'Scheduling',
    MARKETPLACE_TYPE: 'Marketplace',
    TAG_TYPE: 'Tags',
    SHIPPER_TYPE: 'Shipper',
};

// SCHEDULING_TYPE is only meaningful for users who can see orders outside the
// single plan-day window — otherwise the server-side query already narrows to a
// single day and the combobox has nothing to filter on. PlanningContext gates
// the query on the same circles (unscheduled and/or past-scheduled).
// SHIPPER_TYPE's options come from the board, so it is dropped when nothing on it was shipped by
// someone else -- otherwise the dropdown renders with All and nothing to pick.
const CATEGORIES_FOR = (circles, hasShippers) => {
    const base = ['ORDER_TYPE', 'WAREHOUSING_TYPE', 'MARKETPLACE_TYPE', 'TAG_TYPE'];
    if (circles?.[PLAN_PAGE_UNSCHEDULED] || circles?.[PLAN_PAGE_PAST_SCHEDULED]) {
        base.splice(2, 0, 'SCHEDULING_TYPE');
    }
    if (hasShippers) {
        base.push('SHIPPER_TYPE');
    }
    return base;
};

// The two scheduling circles are independent: the Unscheduled option only shows
// for users with the unscheduled circle, and the Missed Orders option only shows
// for users with the past-scheduled circle. All and Scheduled always show when
// the dropdown is rendered.
const optionEntriesFor = (category, circles, options) => {
    const entries = Object.entries(options || {});
    if (category !== 'SCHEDULING_TYPE') return entries;
    return entries.filter(([key]) => {
        if (key === 'UNSCHEDULED') return !!circles?.[PLAN_PAGE_UNSCHEDULED];
        if (key === 'MISSED') return !!circles?.[PLAN_PAGE_PAST_SCHEDULED];
        return true;
    });
};

// A selection can outlive its option -- shipper options come from the board, so changing the plan
// date can strip one out -- and dropping it here keeps the collapsed label from reading as a bare
// uuid and the filter count from counting a filter the pipeline is no longer applying.
const selectedOptionsFor = (categoryState, options) => {
    const selected = Object.entries(categoryState || {})
        .filter(([, value]) => value)
        .map(([key]) => key)
        .filter((key) => !options || key in options);
    return selected.length > 0 ? selected : ['ALL'];
};

const ASSIGNMENT_FILTER_LABELS = {
    ALL: 'All',
    ASSIGNED: 'Assigned',
    UNASSIGNED: 'Unassigned',
};

const ROUTE_FILTER_ALL_LABEL = 'All';

// MUI v4 anchors the dropdown menu to the selected MenuItem by default, which
// makes the menu visibly shift on every selection change. `getContentAnchorEl:
// null` plus explicit anchor/transform origins pin the menu below the Select
// input so the options stay put.
const MENU_PROPS = {
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const SELECT_CSS = css`
    & .MuiInputBase-input {
        padding-top: 8px;
        padding-bottom: 8px;
    }
    && .MuiOutlinedInput-notchedOutline {
        border-color: rgba(0, 0, 0, 0.23);
        border-width: 1px;
    }
`;

const PlanningOrders = () => {
    const { setSelectedOrders, selectedOrders, toggleSelectOrder } = useContext(PlanningSelectionContext);
    const { setModalOpen, setActionableOrder } = useContext(PlanningUiContext);
    const {
        filteredOrders,
        orderCategories,
        toggleOrderCategory,
        searchText,
        setSearchText,
        assignmentFilter,
        setAssignmentFilter,
        routeFilter,
        setRouteFilter,
        routeFilterOptions,
        filterOptionsByCategory,
    } = useContext(PlanningFilterContext);
    const {
        state: { routes },
    } = useContext(PlanningDataContext);
    const { circles } = useClientUser();

    const visibleCategories = useMemo(
        () => CATEGORIES_FOR(circles, Object.keys(filterOptionsByCategory.SHIPPER_TYPE || {}).length > 1),
        [circles, filterOptionsByCategory]
    );

    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const listRef = useRef(null);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [filteredOrders]);

    const activeFilterCount = useMemo(() => {
        let count = assignmentFilter !== 'ALL' ? 1 : 0;
        if (routeFilter !== ROUTE_FILTER_ALL) count++;
        for (const category of visibleCategories) {
            if (!selectedOptionsFor(orderCategories?.[category], filterOptionsByCategory[category]).includes('ALL'))
                count++;
        }
        return count;
    }, [assignmentFilter, routeFilter, visibleCategories, orderCategories, filterOptionsByCategory]);

    const checked = useMemo(() => {
        const allChecked = filteredOrders.every((order) => selectedOrders[`${order.order_id}_${order.crossdock_leg}`]);
        const numSelected = Object.values(selectedOrders).reduce((count, val) => {
            if (val) count++;
            return count;
        }, 0);

        return allChecked && numSelected === filteredOrders.length;
    }, [selectedOrders, filteredOrders]);

    const onSelectAll = (e) => {
        e.stopPropagation();
        if (!checked) {
            setSelectedOrders(
                Object.fromEntries(filteredOrders.map((order) => [`${order.order_id}_${order.crossdock_leg}`, true]))
            );
        } else {
            setSelectedOrders({});
        }
    };

    // Reproduces OrderListItem's legacy fallback: route_color, else the palette
    // color at the route's index within state.routes. 'black' keeps the value
    // truthy past the palette's end so assigned rows never lose their check.
    const routeColorById = useMemo(
        () => Object.fromEntries(routes.map((r, i) => [r.route_id, r.route_color || ROUTE_COLORS[i] || 'black'])),
        [routes]
    );

    const routeLabelById = useMemo(
        () => Object.fromEntries(routeFilterOptions.map(({ value, label }) => [value, label])),
        [routeFilterOptions]
    );

    // Everything the row renderer needs rides on itemData so the renderer
    // itself keeps a stable identity — react-window remounts every visible row
    // when its children component type changes.
    const itemData = useMemo(
        () => ({
            orders: filteredOrders,
            selectedOrders,
            routeColorById,
            toggleSelectOrder,
            setActionableOrder,
            setModalOpen,
        }),
        [filteredOrders, selectedOrders, routeColorById, toggleSelectOrder, setActionableOrder, setModalOpen]
    );

    const order = useCallback(({ index, data, style }) => {
        if (index > data.orders.length - 1) {
            return null;
        }

        const o = data.orders[index];
        const routeId = o.crossdock_leg === 'pickup' ? o.pickup_route_id : o.dropoff_route_id;
        return (
            <OrderListItem
                style={style}
                order={o}
                key={o.order_id}
                isSelected={!!data.selectedOrders[`${o.order_id}_${o.crossdock_leg}`]}
                assignedRouteColor={routeId ? data.routeColorById[routeId] : undefined}
                toggleSelectOrder={data.toggleSelectOrder}
                setActionableOrder={data.setActionableOrder}
                setModalOpen={data.setModalOpen}
            />
        );
    }, []);

    // MUI's multi-select onChange hands back the full new array; toggleOrderCategory
    // is a single-option toggle so we diff against the current selection and pass
    // the one key that changed.
    const handleCategoryChange = (category) => (e) => {
        e.stopPropagation();
        const nextArray = e.target.value;
        if (!Array.isArray(nextArray)) return;
        const options = filterOptionsByCategory[category];
        const currentArray = selectedOptionsFor(orderCategories?.[category], options);
        const added = nextArray.find((k) => !currentArray.includes(k));
        const removed = currentArray.find((k) => !nextArray.includes(k));
        const toggled = added || removed;
        if (toggled) {
            // The keys let toggleOrderCategory collapse "every option selected" to ALL for a
            // category whose options it cannot look up itself.
            toggleOrderCategory(category, toggled, Object.keys(options || {}));
            setSelectedOrders({});
        }
    };

    return (
        <>
            <PlanningListItem
                css={css`
                    border-top: 1px solid #e2e2e2;
                    border-bottom: none;
                `}
            >
                <SearchInput onChange={(s) => setSearchText(s)} initialValue={searchText} placeholder="Search Orders" />
            </PlanningListItem>

            <PlanningListItem hover="true" onClick={() => setFiltersExpanded((prev) => !prev)}>
                <GridItemRow>
                    <Body1>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</Body1>
                    {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
                </GridItemRow>
            </PlanningListItem>

            {filtersExpanded && (
                <>
                    <PlanningListItem>
                        <FormControl variant="outlined" fullWidth>
                            <Select
                                labelId="assignment-filter-select"
                                id="assignment-filter-select"
                                inputProps={{ 'aria-label': 'Assignment' }}
                                value={assignmentFilter}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    const next = e.target.value;
                                    if (next) {
                                        setAssignmentFilter(next);
                                        setSelectedOrders({});
                                    }
                                }}
                                MenuProps={MENU_PROPS}
                                renderValue={(value) => (
                                    <Body2>Assignment: {ASSIGNMENT_FILTER_LABELS[value] || value}</Body2>
                                )}
                                css={SELECT_CSS}
                            >
                                {Object.entries(ASSIGNMENT_FILTER_LABELS).map(([key, label]) => (
                                    <MenuItem value={key} key={key}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </PlanningListItem>

                    {routeFilterOptions.length > 0 && (
                        <PlanningListItem>
                            <FormControl variant="outlined" fullWidth>
                                <Select
                                    labelId="route-filter-select"
                                    id="route-filter-select"
                                    inputProps={{ 'aria-label': 'Route' }}
                                    value={routeFilter}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        const next = e.target.value;
                                        if (next) {
                                            setRouteFilter(next);
                                            setSelectedOrders({});
                                        }
                                    }}
                                    MenuProps={MENU_PROPS}
                                    renderValue={(value) => (
                                        <Body2>Route: {routeLabelById[value] || ROUTE_FILTER_ALL_LABEL}</Body2>
                                    )}
                                    css={SELECT_CSS}
                                >
                                    <MenuItem value={ROUTE_FILTER_ALL} key={ROUTE_FILTER_ALL}>
                                        {ROUTE_FILTER_ALL_LABEL}
                                    </MenuItem>
                                    {routeFilterOptions.map(({ value, label }) => (
                                        <MenuItem value={value} key={value}>
                                            <LocalShippingOutlinedIcon
                                                fontSize="small"
                                                css={css`
                                                    color: ${routeColorById[value] || 'black'};
                                                    margin-right: 5px;
                                                `}
                                            />
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </PlanningListItem>
                    )}

                    {visibleCategories.map((category) => {
                        const options = filterOptionsByCategory[category];
                        const selected = selectedOptionsFor(orderCategories?.[category], options);
                        const optionEntries = optionEntriesFor(category, circles, options);
                        return (
                            <PlanningListItem key={category}>
                                <FormControl variant="outlined" fullWidth>
                                    <Select
                                        multiple
                                        labelId={`${category}-select`}
                                        id={`${category}-select`}
                                        inputProps={{ 'aria-label': CATEGORY_LABELS[category] }}
                                        value={selected}
                                        onChange={handleCategoryChange(category)}
                                        MenuProps={MENU_PROPS}
                                        renderValue={(value) => {
                                            const labels = value.map((k) => options?.[k]?.label || k).join(', ');
                                            return (
                                                <Body2>
                                                    {CATEGORY_LABELS[category]}: {labels}
                                                </Body2>
                                            );
                                        }}
                                        css={SELECT_CSS}
                                    >
                                        {optionEntries.map(([key, { label }]) => (
                                            <MenuItem value={key} key={key}>
                                                <Checkbox
                                                    checked={selected.includes(key)}
                                                    size="small"
                                                    color="primary"
                                                />
                                                <Body2>{label}</Body2>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </PlanningListItem>
                        );
                    })}
                </>
            )}

            <PlanningListItem>
                <FormControlLabel
                    css={css`
                        width: 100%;
                        margin-right: 0;
                        .MuiFormControlLabel-label {
                            flex-grow: 1;
                        }
                    `}
                    control={<Checkbox onMouseDown={onSelectAll} color="primary" checked={checked} />}
                    label={<Body2>Select All ({filteredOrders.length})</Body2>}
                />
            </PlanningListItem>

            <Grid
                container
                css={css`
                    display: flex;
                    flex-grow: 1;
                    flex-basis: 0;
                `}
            >
                <AutoSizer>
                    {({ height, width }) => {
                        return (
                            <VariableSizeList
                                ref={listRef}
                                overscanCount={20}
                                height={height}
                                itemData={itemData}
                                itemCount={filteredOrders.length + 1}
                                itemSize={(index) => {
                                    let size = 265;
                                    const item = filteredOrders[index];
                                    if (item?.reference_id) {
                                        size += 15;
                                    }
                                    if (item?.notes?.length) {
                                        const deliveryNotes = item.notes.filter(
                                            (note) => note.type === 'Delivery'
                                        ).length;
                                        if (deliveryNotes > 0) {
                                            size += deliveryNotes * 42;
                                        }
                                    }
                                    return size;
                                }}
                                width={width}
                            >
                                {order}
                            </VariableSizeList>
                        );
                    }}
                </AutoSizer>
            </Grid>

            <Grid
                container
                css={css`
                    padding: 1rem;
                    gap: 0.5rem;
                `}
            >
                <SecondaryButtonFullW
                    disabled={!Object.values(selectedOrders).some(Boolean)}
                    onClick={() => setModalOpen(MODALS.BATCH_TIMEFRAME_CONFIRMATION)}
                >
                    Send Timeframe Confirmations
                </SecondaryButtonFullW>
                <PrimaryButtonFullW onClick={() => setModalOpen(MODALS.ORDER_OMS)}>
                    Back to Unassigned
                </PrimaryButtonFullW>
            </Grid>
        </>
    );
};

export default PlanningOrders;
